import { SummaryPanel } from '@components/panels/SummaryPanel'
import { FormField } from '@components/shared/FormField'
import { CurrencyFormat } from '@components/shared/Format'
import { I18n } from '@components/shared/I18n'
import { Table, TableCell, TableRow } from '@components/shared/Table'
import { WithTooltip } from '@components/shared/WithTooltip'
import { store } from '@stores/Store'
import { Form } from 'antd'
import { observer } from 'mobx-react'
import React, { FunctionComponent } from 'react'
import { useMobile } from 'src/hooks/useMobile'
import styles from './SummaryCorePlusIncome.module.scss'

interface SummaryCorePlusIncomeFormData {
  bronzeBuilderActive: boolean
  bronzeFoundationActive: boolean
  bronzeConsistencyIncentiveActive: boolean
  performancePlusActive: boolean
  leadershipPlusIncentiveActive: boolean
  twoTimeCashIncentiveActive: boolean
}

export const SummaryCorePlusIncome: FunctionComponent = observer(() => {
  const {
    bronzeIncentivesStore, bronzeConsistencyIncentiveStore,
    performancePlusStore, leadershipPlusIncentiveStore,
    twoTimeCashIncentiveStore
  } = store

  const { bronzeFoundationMonthlyBonusAmount, bronzeFoundationAnnualBonusAmount, bronzeBuilderMonthlyBonusAmount, bronzeBuilderAnnualBonusAmount } = store.bronzeIncentiveCalculation
  const { bronzeBuilderConsitencyIncentiveAmount } = store.bronzeConsistencyIncentiveCalculation
  const { performancePlusMonthlyAmount, performancePlusAnnualAmount } = store.performancePlusCalculation
  const { leadershipPlusIncentiveAnnualIncentiveAmount } = store.leadershipPlusIncentiveCalculation
  const { twoTimeCashIncentiveAmount } = store.twoTimeCashIncentiveCalculation
  const { totalCorePlusMonthlyAmount, totalCorePlusAnnualAmount } = store.summaryCalculation

  const { bronzeBuilderActive, bronzeFoundationActive, bronzeBuilderEnabled, bronzeFoundationEnabled } = bronzeIncentivesStore
  const { bronzeConsistencyIncentiveActive } = bronzeConsistencyIncentiveStore
  const { performancePlusActive } = performancePlusStore
  const { leadershipPlusIncentiveActive } = leadershipPlusIncentiveStore
  const { twoTimeCashIncentiveActive } = twoTimeCashIncentiveStore

  const initialValues: SummaryCorePlusIncomeFormData = {
    bronzeBuilderActive, bronzeFoundationActive, bronzeConsistencyIncentiveActive,
    performancePlusActive, leadershipPlusIncentiveActive, twoTimeCashIncentiveActive
  }

  const { isMobile } = useMobile()
  const tdTitleStyle = { width: isMobile ? 120 : 340 }
  const tdMonthlyStyle = { width: isMobile ? 80 : 160 }
  const tdAnnualStyle = { width: isMobile ? 88 : 160 }

  const onValuesChange = (data: SummaryCorePlusIncomeFormData) => {
    const { bronzeIncentivesStore, bronzeConsistencyIncentiveStore,
      performancePlusStore, leadershipPlusIncentiveStore,
      twoTimeCashIncentiveStore
    } = store

    if ('bronzeBuilderActive' in data) { bronzeIncentivesStore.setBronzeBuilderActive(data.bronzeBuilderActive) }
    if ('bronzeFoundationActive' in data) { bronzeIncentivesStore.setBronzeFoundationActive(data.bronzeFoundationActive) }
    if ('bronzeConsistencyIncentiveActive' in data) { bronzeConsistencyIncentiveStore.setBronzeConsistencyIncentiveActive(data.bronzeConsistencyIncentiveActive) }
    if ('performancePlusActive' in data) { performancePlusStore.setPerformancePlusActive(data.performancePlusActive) }
    if ('leadershipPlusIncentiveActive' in data) { leadershipPlusIncentiveStore.setLeadershipPlusIncentiveActive(data.leadershipPlusIncentiveActive) }
    if ('twoTimeCashIncentiveActive' in data) { twoTimeCashIncentiveStore.setTwoTimeCashIncentiveActive(data.twoTimeCashIncentiveActive) }
  }

  return (
    <>
      <div className={styles['wrapper']}>
        <Form initialValues={initialValues} onValuesChange={onValuesChange}>
          <Table>
            {/* Header */}
            <TableRow>
              <TableCell paddingLevel='small' width={24}>&nbsp;</TableCell>
              <TableCell style={tdTitleStyle}>
                <h4 className={styles['left']}><WithTooltip title='TOTAL_INCOME_TOOLTIP' iconPosition='right' iconFloat='right'><I18n name='CORE_PLUS_INCOME' /></WithTooltip></h4>
              </TableCell>
              <TableCell align='right' style={tdMonthlyStyle}><I18n name='MONTHLY_INCOME' /></TableCell>
              <TableCell align='right' style={tdAnnualStyle}><I18n name='ANNUAL_INCOME' /></TableCell>
            </TableRow>

            {/* Core Plus⁺ Bronze Foundation */}
            <TableRow disabled={!bronzeFoundationEnabled}>
              <TableCell paddingLevel='small'><FormField name={'bronzeFoundationActive'} type='checkbox' disabled={!bronzeFoundationEnabled} /></TableCell>
              <TableCell paddingLevel='small'><I18n name="CORE_PLUS_BRONZE_FOUNDATION" /></TableCell>
              <TableCell align='right'><CurrencyFormat value={bronzeFoundationMonthlyBonusAmount} /></TableCell>
              <TableCell align='right'><WithTooltip title='CORE_PLUS_BRONZE_FOUNDATION_TOOLTIP' iconPosition='right'><CurrencyFormat value={bronzeFoundationAnnualBonusAmount} /></WithTooltip></TableCell>
            </TableRow>

            {/* Core Plus⁺ Bronze Builder */}
            <TableRow disabled={!bronzeBuilderEnabled}>
              <TableCell paddingLevel='small'><FormField name={'bronzeBuilderActive'} type='checkbox' disabled={!bronzeBuilderEnabled} /></TableCell>
              <TableCell paddingLevel='small'><I18n name="CORE_PLUS_BRONZE_BUILDER" /></TableCell>
              <TableCell align='right'><CurrencyFormat value={bronzeBuilderMonthlyBonusAmount} /></TableCell>
              <TableCell align='right'><WithTooltip title='CORE_PLUS_BRONZE_BUILDER_TOOLTIP' iconPosition='right'><CurrencyFormat value={bronzeBuilderAnnualBonusAmount} /></WithTooltip></TableCell>
            </TableRow>

            {/* Core Plus⁺ Bronze Builder Consistency */}
            <TableRow>
              <TableCell paddingLevel='small'><FormField name={'bronzeConsistencyIncentiveActive'} type='checkbox' /></TableCell>
              <TableCell paddingLevel='small'><I18n name="CORE_PLUS_BRONZE_BUILDER_CONSISTENCY" /></TableCell>
              <TableCell align='right'>&nbsp;</TableCell>
              <TableCell align='right'><WithTooltip title="CORE_PLUS_BRONZE_BUILDER_CONSISTENCY_TOOLTIP" iconPosition='right'><CurrencyFormat value={bronzeBuilderConsitencyIncentiveAmount} /></WithTooltip></TableCell>
            </TableRow>

            {/* Core Plus⁺ New Performance Plus Incentive⁺ */}
            <TableRow>
              <TableCell paddingLevel='small'><FormField name={'performancePlusActive'} type='checkbox' /></TableCell>
              <TableCell paddingLevel='small'><I18n name="CORE_PLUS_NEW_PERFORMANCE_PLUS" /></TableCell>
              <TableCell align='right'><CurrencyFormat value={performancePlusMonthlyAmount} /></TableCell>
              <TableCell align='right'><WithTooltip title='CORE_PLUS_NEW_PERFORMANCE_PLUS_TOOLTIP' iconPosition='right'><CurrencyFormat value={performancePlusAnnualAmount} /></WithTooltip></TableCell>
            </TableRow>

            {/* 	Core Plus⁺ Leadership Plus Incentive⁺ */}
            <TableRow>
              <TableCell paddingLevel='small'><FormField name={'leadershipPlusIncentiveActive'} type='checkbox' /></TableCell>
              <TableCell paddingLevel='small'><I18n name="CORE_PLUS_LEADERSHIP_PLUS" /></TableCell>
              <TableCell align='right'>&nbsp;</TableCell>
              <TableCell align='right'><WithTooltip title="CORE_PLUS_LEADERSHIP_PLUS_TOOLTIP" iconPosition='right'><CurrencyFormat value={leadershipPlusIncentiveAnnualIncentiveAmount} /></WithTooltip></TableCell>
            </TableRow>

            {/* Core Plus⁺ Two-Time Cash Award */}
            <TableRow>
              <TableCell paddingLevel='small'><FormField name={'twoTimeCashIncentiveActive'} type='checkbox' /></TableCell>
              <TableCell paddingLevel='small'><I18n name="CORE_PLUS_TWO_TIME_CASH_AWARD" /></TableCell>
              <TableCell align='right'>&nbsp;</TableCell>
              <TableCell align='right'><WithTooltip title='CORE_PLUS_TWO_TIME_CASH_AWARD_TOOLTIP' iconPosition='right'><CurrencyFormat value={twoTimeCashIncentiveAmount} /></WithTooltip></TableCell>
            </TableRow>
          </Table>
        </Form>
      </div>

      <SummaryPanel title='CORE_PLUS_VALUE' monthlyAmount={totalCorePlusMonthlyAmount} annualAmount={totalCorePlusAnnualAmount} />
    </>
  )
})
